import { PayType } from "@api/memberApi";
import log from "logbeacon";
import { pubsub } from "utils";
import { LOCAL_STORAGE_KEYS } from "@/src/config";
import { pubSubKeyMap } from "@/src/utils/pubSubKeyMap";

export interface PaymentErrorProcessorOptions {
  storageKey?: string;
  isLoggedIn: boolean;
  onPaymentErrorChange?: (
    paymentErrorId: string,
    paymentUrl: string,
    payChannel: string,
  ) => void;
}

export interface PaymentErrorResult {
  hasError: boolean;
  paymentErrorId: string;
  paymentData?: any;
}

/**
 * 处理支付错误状态的公共逻辑
 * @param paymentListData 支付列表数据
 * @param options 配置选项
 * @returns 处理结果
 */
const processPaymentErrorStatus = async (
  paymentListData: any[],
  options: PaymentErrorProcessorOptions,
): Promise<PaymentErrorResult> => {
  const {
    storageKey = LOCAL_STORAGE_KEYS.NEXT_PAYMENT_ERROR,
    isLoggedIn,
    onPaymentErrorChange,
  } = options;

  const hasCloseValue = await localStorage.getItem(storageKey);
  const currentPaymentListData = paymentListData || [];

  const paymentDataLength = currentPaymentListData.length;
  const hasError = paymentDataLength > 0;

  // 有错误，则本地存储id和错误
  // public enum SubStatus {
  // PAYMENT_FAILED("PAYMENT_FAILED"),
  // USER_CANCELLED("USER_CANCELLED"),
  // UPGRADE("UPGRADE"),
  // DOWNGRADE("DOWNGRADE"),
  // ;
  const paymentData = currentPaymentListData[0];
  const hasClosed =
    hasCloseValue && hasCloseValue === `${paymentData?.id}_CLOSE`;
  const saveLocalError =
    !hasClosed &&
    hasError &&
    paymentData?.sub_status === "PAYMENT_FAILED" &&
    isLoggedIn;

  const paymentErrorId = saveLocalError ? paymentData?.id : "";
  const paymentUrl =
    paymentData?.links?.find((link: any) => link.rel === "customer_session_url")
      ?.href || "";

  if (saveLocalError) {
    localStorage.setItem(storageKey, `${paymentData?.id}_OPEN`);
  }

  // 通知状态变化
  if (onPaymentErrorChange) {
    onPaymentErrorChange(paymentErrorId, paymentUrl, paymentData?.pay_channel);
  }

  return {
    hasError: !!paymentErrorId,
    paymentErrorId,
    paymentData,
  };
};

/**
 * 创建支付错误状态获取函数
 * @param getListPayment 获取支付列表的函数
 * @param options 配置选项
 * @returns 获取支付错误状态的函数
 */
export const createPaymentErrorFetcher = (
  getListPayment: (params: any) => Promise<any[]>,
  options: PaymentErrorProcessorOptions,
) => {
  return async (
    refreshPaymentListData?: any[],
  ): Promise<PaymentErrorResult> => {
    let paymentListData = refreshPaymentListData;

    if (!paymentListData) {
      try {
        paymentListData = await getListPayment({
          pay_type: PayType.CONTINUOUS,
          status: ["SUSPENDED"] as any,
        });

        // 通知侧边栏进行数据更新
        pubsub.emit(pubSubKeyMap.MEMBER_PAYMENT_ERROR, paymentListData);
      } catch (error) {
        log.error("[paymentErrorUtils] failed to fetch payment data:", error);
        paymentListData = [];
      }
    }

    return await processPaymentErrorStatus(paymentListData, options);
  };
};

/**
 * 关闭支付错误提示的工具函数
 * @param paymentErrorId 支付错误ID
 * @param storageKey 存储键
 * @param onPaymentErrorChange 状态变化回调
 */
export const closePaymentError = (
  paymentErrorId: string,
  storageKey: string = LOCAL_STORAGE_KEYS.NEXT_PAYMENT_ERROR,
  onPaymentErrorChange?: (paymentErrorId: string) => void,
) => {
  if (paymentErrorId) {
    localStorage.setItem(storageKey, `${paymentErrorId}_CLOSE`);
    if (onPaymentErrorChange) {
      onPaymentErrorChange("");
    }
  }
};

/**
 * 为pubsub事件创建监听器
 * @param fetchPaymentErrorStatus 获取支付错误状态的函数
 * @param isLoggedIn 是否已登录
 * @returns 取消监听的函数
 */
export const createPaymentErrorListener = (
  fetchPaymentErrorStatus: (data?: any[]) => Promise<PaymentErrorResult>,
  isLoggedIn: boolean,
) => {
  const updateMemberPaymentErrorUnsubscribe = pubsub.on(
    pubSubKeyMap.MEMBER_PAYMENT_ERROR,
    (paymentListData: any) => {
      if (isLoggedIn) {
        fetchPaymentErrorStatus(paymentListData);
      }
    },
  );

  return updateMemberPaymentErrorUnsubscribe;
};
